export const BILLING_CATEGORIES = [
  "Crown & Bridge",
  "Crown & Bridge – Implant / Smile Design",
  "Crown & Bridge – With Model",
  "Crown & Bridge – Grade II",
  "Crown & Bridge – Grade II With Model",
  "Removables",
  "Digital Models",
  "Framework",
  "Appliance",
  "Surgical Guide",
  "Others",
] as const;

export const JOB_CARD_EMPLOYEE_ROLES = [
  "Designer",
  "Senior Designer",
  "Captain",
  "Assistant Captain",
  "Super Admin",
] as const;

export const UNITS_QC2_ROLES = [
  "Captain",
  "Assistant Captain",
  "CRM",
  "Senior Designer",
  "Super Admin",
] as const;
export const UNITS_CRM_ROLES = ["CRM", "Super Admin"] as const;

const SERVICE_KEY_TO_TREATMENT: Record<string, string> = {
  crownAndBridge: "Crown & Bridge",
  Implant: "Implant",
  smileDesign: "Smile Design",
  veneers: "No-prep Veneers",
  "complete-denture": "Complete Denture",
  "partial-denture": "Partial Denture",
  "partial-cast": "RPD Framework",
  surgicalGuide: "Surgical Guide",
  "nightguard-bite-splint": "Nightguard",
  "digital-model": "Digital Model",
  otherTreatments: "Other Treatment",
  filledOrder: "Other Treatment",
};

const TREATMENT_ALIASES: Record<string, string> = {
  "crown & bridge": "Crown & Bridge",
  implant: "Implant",
  "smile design": "Smile Design",
  "smile design / mock-up": "Smile Design",
  "no-prep veneers": "No-prep Veneers",
  "complete denture": "Complete Denture",
  "ldp complete denture": "Complete Denture",
  "partial denture": "Partial Denture",
  "ldp partial denture": "Partial Denture",
  "digital model": "Digital Model",
  "digital models": "Digital Model",
  "rpd framework": "RPD Framework",
  framework: "RPD Framework",
  nightguard: "Nightguard",
  "nightguard / bite splint": "Nightguard",
  appliance: "Nightguard",
  "surgical guide": "Surgical Guide",
  "other treatment": "Other Treatment",
  "other treatments": "Other Treatment",
  others: "Other Treatment",
  "upload filled order": "Other Treatment",
};

const dashNorm = (value: string) => value.replace(/[\u2013\u2014]/g, "-");

export const canonicalBillingCategory = (value?: string | null): string => {
  const v = String(value || "").trim();
  if (!v) return "";
  return (
    (BILLING_CATEGORIES as readonly string[]).find(
      (item) => item === v || dashNorm(item) === dashNorm(v),
    ) || ""
  );
};

export const isBillingCategory = (value?: string | null): boolean =>
  Boolean(canonicalBillingCategory(value));

export const parseTreatments = (serviceType?: string | null): string[] => {
  const seen = new Set<string>();
  String(serviceType || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const mapped =
        SERVICE_KEY_TO_TREATMENT[part] ||
        TREATMENT_ALIASES[part.toLowerCase()] ||
        "";
      if (mapped) seen.add(mapped);
    });
  return [...seen];
};

export const formatTreatments = (serviceType?: string | null): string =>
  parseTreatments(serviceType).join(", ");

export const suggestBillingCategory = ({
  serviceType,
  treatments,
  units = 0,
  model = false,
}: {
  serviceType?: string | null;
  treatments?: string[];
  units?: number;
  model?: boolean;
} = {}): string => {
  const list = treatments?.length ? treatments : parseTreatments(serviceType);
  const has = (name: string) => list.includes(name);
  const hasCb = has("Crown & Bridge");
  const implantFamily =
    has("Implant") || has("Smile Design") || has("No-prep Veneers");
  const hasDigital = model || has("Digital Model");
  const gradeII = Number(units) > 5;

  if (hasCb || implantFamily) {
    if (hasCb && hasDigital && gradeII) {
      return "Crown & Bridge – Grade II With Model";
    }
    if (hasCb && gradeII) return "Crown & Bridge – Grade II";
    if (hasCb && hasDigital) return "Crown & Bridge – With Model";
    if (implantFamily) return "Crown & Bridge – Implant / Smile Design";
    return "Crown & Bridge";
  }
  if (has("Complete Denture") || has("Partial Denture")) return "Removables";
  if (has("RPD Framework")) return "Framework";
  if (has("Nightguard")) return "Appliance";
  if (has("Surgical Guide")) return "Surgical Guide";
  if (has("Digital Model")) return "Digital Models";
  return "Others";
};

export const resolveBillingCategory = (
  savedType?: string | null,
  options?: {
    serviceType?: string | null;
    units?: number;
    model?: boolean;
  },
): string => {
  const saved = canonicalBillingCategory(savedType);
  if (saved) return saved;
  const fromSaved = parseTreatments(savedType);
  const fromService = parseTreatments(options?.serviceType);
  return suggestBillingCategory({
    treatments: [...new Set([...fromService, ...fromSaved])],
    units: options?.units,
    model: options?.model,
  });
};

const collectToothValues = (value: unknown, out: string[]) => {
  if (value == null || value === "") return;
  if (Array.isArray(value)) {
    value.forEach((item) => collectToothValues(item, out));
    return;
  }
  if (typeof value === "object") {
    Object.values(value as Record<string, unknown>).forEach((item) =>
      collectToothValues(item, out),
    );
    return;
  }
  const text = String(value).trim();
  if (text && text !== "[object Object]") out.push(text);
};

export const MODEL_TYPES = [
  "normal",
  "preop",
  "gingiva",
  "abutment",
  "nightguard",
  "splint",
  "special_tray",
] as const;

export type ModelType = (typeof MODEL_TYPES)[number];

export const MODEL_TYPE_OPTIONS: { value: ModelType; label: string }[] = [
  { value: "normal", label: "Normal" },
  { value: "preop", label: "Preop" },
  { value: "gingiva", label: "Gingiva" },
  { value: "abutment", label: "Abutment" },
  { value: "nightguard", label: "Nightguard" },
  { value: "splint", label: "Splint" },
  { value: "special_tray", label: "Special tray" },
];

export const MODEL_CATEGORY_TYPES = ["normal", "preop"] as const;
export const OTHER_ITEM_TYPES = [
  "gingiva",
  "abutment",
  "nightguard",
  "splint",
  "special_tray",
] as const;

export type ModelCategoryType = (typeof MODEL_CATEGORY_TYPES)[number];
export type OtherItemType = (typeof OTHER_ITEM_TYPES)[number];

export const MODEL_CATEGORY_OPTIONS = MODEL_TYPE_OPTIONS.filter((option) =>
  (MODEL_CATEGORY_TYPES as readonly string[]).includes(option.value),
);
export const OTHER_ITEM_OPTIONS = MODEL_TYPE_OPTIONS.filter((option) =>
  (OTHER_ITEM_TYPES as readonly string[]).includes(option.value),
);

export const isModelCategoryType = (
  type?: string | null,
): type is ModelCategoryType =>
  (MODEL_CATEGORY_TYPES as readonly string[]).includes(
    canonicalModelType(type),
  );

export const isOtherItemType = (type?: string | null): type is OtherItemType =>
  (OTHER_ITEM_TYPES as readonly string[]).includes(canonicalModelType(type));

export const canonicalModelType = (value?: string | null): ModelType | "" => {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  if (
    v === "special_tray" ||
    v === "special-tray" ||
    v === "special tray" ||
    v === "specialtray"
  ) {
    return "special_tray";
  }
  return (MODEL_TYPES as readonly string[]).includes(v) ? (v as ModelType) : "";
};

const expandModelTypeToken = (raw: string): ModelType[] => {
  const v = raw.trim().toLowerCase();
  if (!v) return [];
  if (v === "nightguard-splint" || v === "nightguard splint") {
    return ["nightguard", "splint"];
  }
  const canonical = canonicalModelType(v);
  return canonical ? [canonical] : [];
};

export const parseModelTypes = (value: unknown): ModelType[] => {
  if (value == null || value === "") return [];
  if (typeof value === "object" && !Array.isArray(value)) {
    const data = value as Record<string, unknown>;
    if (Array.isArray(data.models)) return parseModelTypes(data.models);
    return parseModelTypes(data.model_types ?? data.model_type);
  }
  const parts: unknown[] = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/[,/|]/)
      : [value];
  return [
    ...new Set(
      parts.flatMap((part) => {
        if (part && typeof part === "object") {
          const row = part as Record<string, unknown>;
          return expandModelTypeToken(
            String(row.type ?? row.model_type ?? ""),
          );
        }
        return expandModelTypeToken(String(part ?? ""));
      }),
    ),
  ];
};

export const serializeModelTypes = (value: unknown): string =>
  parseModelTypes(value).join(", ");

export const modelTypeLabel = (type?: string | null): string =>
  MODEL_TYPE_OPTIONS.find((option) => option.value === canonicalModelType(type))
    ?.label || "";

export const formatModelTypes = (value: unknown): string =>
  parseModelTypes(value)
    .map((type) => modelTypeLabel(type) || type)
    .join(", ");

export const parseModelCategoryTypes = (value: unknown): ModelType[] =>
  parseModelTypes(value).filter(isModelCategoryType);

export const parseOtherItemTypes = (value: unknown): ModelType[] =>
  parseModelTypes(value).filter(isOtherItemType);

export const persistModelTypes = (
  modelYes: boolean,
  types: unknown,
): ModelType[] => {
  const selected = new Set(parseModelTypes(types));
  return MODEL_TYPES.filter((type) => {
    if (isOtherItemType(type)) return selected.has(type);
    return modelYes && selected.has(type);
  });
};

export const hasModelCategorySelection = (types: unknown) =>
  parseModelCategoryTypes(types).length > 0;

export const MODEL_TYPE_UNITS: Record<ModelType, number> = {
  normal: 1,
  preop: 1,
  gingiva: 1,
  abutment: 1,
  nightguard: 2,
  splint: 2,
  special_tray: 3,
};

export const modelTypeUnitRate = (type?: string | null) =>
  MODEL_TYPE_UNITS[canonicalModelType(type) as ModelType] || 0;

export const modelWorkUnitsFromTypes = (types?: unknown) =>
  parseModelTypes(types).reduce(
    (sum, type) => sum + (MODEL_TYPE_UNITS[type] || 0),
    0,
  );

export const hasModelWorkSelection = (types?: unknown) =>
  modelWorkUnitsFromTypes(types) > 0;

export const modelTypeAddsDeliveryUnit = (type?: string | null) => {
  const canonical = canonicalModelType(type);
  return canonical === "normal" || canonical === "abutment";
};

export const modelDesignerUnits = (modelYes: boolean, types?: unknown) => {
  const fromTypes = modelWorkUnitsFromTypes(types);
  if (fromTypes > 0) return fromTypes;
  return modelYes ? 1 : 0;
};

export const modelCategoryWorkUnits = (types?: unknown) =>
  parseModelCategoryTypes(types).reduce(
    (sum, type) => sum + (MODEL_TYPE_UNITS[type] || 0),
    0,
  );

export const modelOtherItemWorkUnits = (types?: unknown) =>
  parseOtherItemTypes(types).reduce(
    (sum, type) => sum + (MODEL_TYPE_UNITS[type] || 0),
    0,
  );

export type DesignerWork = "normal" | "model";

const splitWorkNames = (value?: string | null) =>
  String(value || "")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);

export const parseDesignerWork = (
  value: unknown,
  names: string[] = [],
  modelBy?: string,
  extrasAreModel?: boolean,
): Record<string, DesignerWork> => {
  const saved =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const work: Record<string, DesignerWork> = {};
  const modelPeople = new Set(splitWorkNames(modelBy));
  names.forEach((name, index) => {
    if (!name) return;
    if (index === 0) {
      work[name] = "normal";
      return;
    }
    if (extrasAreModel === true) {
      work[name] = "model";
      return;
    }
    if (extrasAreModel === false) {
      work[name] = "normal";
      return;
    }
    const raw = String(saved[name] || "").toLowerCase();
    work[name] =
      raw === "model" || modelPeople.has(name) ? "model" : "normal";
  });
  return work;
};

export const designerWorkModelPeople = (
  names: string[],
  work?: Record<string, DesignerWork> | null,
) => names.slice(1).filter((name) => work?.[name] === "model");

export const designerWorkModelBy = (
  names: string[],
  work?: Record<string, DesignerWork> | null,
) => designerWorkModelPeople(names, work).join(", ");

export const modelUnitsByPerson = (
  modelYes: boolean,
  types: unknown,
  names: string[],
  work?: Record<string, DesignerWork> | null,
  fallbackBy?: string,
  unitsByName?: Record<string, number> | null,
  explicitUnits?: number | null,
): Record<string, number> => {
  const extras = designerWorkModelPeople(names, work);
  const calculated = modelDesignerUnits(modelYes, types);
  const override = Number(explicitUnits);
  const out: Record<string, number> = {};

  for (const name of extras) {
    if (!name) continue;
    out[name] = Math.max(Number(unitsByName?.[name]) || 0, 1);
  }

  const primaryName = names[0] || splitWorkNames(fallbackBy)[0] || "";
  const primaryUnits = extras.length
    ? Number.isFinite(override) && override >= 0
      ? override
      : modelCategoryWorkUnits(types)
    : Number.isFinite(override) && override >= 1
      ? override
      : calculated;

  if (primaryName && primaryUnits > 0 && extras.every((name) => name !== primaryName)) {
    out[primaryName] = (out[primaryName] || 0) + primaryUnits;
  }

  if (Object.keys(out).length) return out;

  const fallback = splitWorkNames(fallbackBy);
  if (fallback.length >= 2) {
    return Object.fromEntries(
      fallback.map((name) => [
        name,
        Math.max(Number(unitsByName?.[name]) || 0, 1),
      ]),
    );
  }
  if (fallback[0] && calculated > 0) {
    return { [fallback[0]]: calculated };
  }
  return {};
};

export const isJuCase = (caseId?: string | null) =>
  String(caseId || "")
    .replace(/^RD-\d+-/, "")
    .toUpperCase()
    .startsWith("JU");

export const parseToothList = (raw: unknown): string[] => {
  const flat = flattenToothNumbers(raw);
  return flat
    ? flat
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
};

export const serializeToothList = (teeth: string[]): string =>
  [...new Set(teeth.map((item) => String(item).trim()).filter(Boolean))].join(
    ", ",
  );

export const sameToothList = (a: unknown, b: unknown) => {
  const left = parseToothList(a)
    .map((item) => item.toLowerCase())
    .sort();
  const right = parseToothList(b)
    .map((item) => item.toLowerCase())
    .sort();
  if (left.length !== right.length) return false;
  return left.every((item, index) => item === right[index]);
};

export const toggleToothNumber = (
  current: string,
  tooth: number | string,
): string => {
  const list = parseToothList(current);
  const next = String(tooth);
  return serializeToothList(
    list.includes(next) ? list.filter((item) => item !== next) : [...list, next],
  );
};

export const flattenToothNumbers = (raw: unknown): string => {
  if (raw == null || raw === "") return "";

  let data: unknown = raw;
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return "";
    try {
      data = JSON.parse(trimmed);
    } catch {
      return trimmed
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .join(", ");
    }
  }

  const teeth: string[] = [];
  collectToothValues(data, teeth);
  return [...new Set(teeth)].join(", ");
};
